import React from 'react';
import Link from 'next/link';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons';
import { PulseCell, Tooltip } from 'src/components/PulseUI';
import { ActivityIcon } from 'src/components/Activity/components';
import { MedRMStatus, PushToCRMGroup } from 'src/components';
import { daysSince } from 'src/utils/daysSince';
import { getProfileUrl } from 'src/utils/profileUrl';
import { format as formatDate } from 'date-fns';

const formatActivityDate = (dateStr: string): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr.split('T')[0] + 'T00:00:00');
  return isNaN(date.getTime()) ? '' : formatDate(date, 'MM/dd/yyyy');
};

interface ActivityProvider {
  id: string;
  name: string;
  provider_id: string;
  specialty: string;
  type: string;
  address_line_1?: string;
  city?: string;
  state?: string;
}

interface ActivityOwner {
  first_name: string;
  last_name: string;
}

interface Activity {
  id: string;
  type: string;
  provider: ActivityProvider;
  owner: ActivityOwner;
  created_at: string;
  activity_date: string;
  details: string;
}

interface ActivityTableRowProps {
  activity: Activity;
  activityType?: MedScout.ActivityType;
  categoryLabel?: string;
  showCategoryColumn?: boolean;
  isManager: boolean;
  showStatusColumn: boolean;
  onOpenProfile: (provider: { providerId: string; type: string }) => void;
  columnVisibilityModel: Record<string, boolean>;
}

const ActivityTableRow = ({
  activity,
  activityType,
  categoryLabel,
  showCategoryColumn = false,
  isManager,
  showStatusColumn,
  onOpenProfile,
  columnVisibilityModel,
}: ActivityTableRowProps) => {

  return (
    <React.Fragment>
      {/* Activity Type */}
      {columnVisibilityModel.type !== false && (
      <PulseCell
        className="bg-white border-b border-b-neutral-200 group-hover:bg-neutral-50"
        data-testid={`activity-row-${activity.id}-type`}
      >
        <div className="flex items-center gap-2 whitespace-nowrap">
          <ActivityIcon
            item={activityType}
            height="1.5rem"
            width="1.5rem"
            iconSize=".75rem"
          />
          <span className="text-primary" aria-label={`Activity type: ${activityType?.label}`}>
            {activityType?.label}
          </span>
        </div>
      </PulseCell>
      )}

      {/* Category */}
      {showCategoryColumn && columnVisibilityModel.category !== false && (
      <PulseCell
        className="bg-white border-b border-b-neutral-200 group-hover:bg-neutral-50"
        data-testid={`activity-row-${activity.id}-category`}
      >
        <span aria-label={categoryLabel ? `Category: ${categoryLabel}` : 'No category'}>
          {categoryLabel || '-'}
        </span>
      </PulseCell>
      )}

      {/* Target (Provider) */}
      {columnVisibilityModel.name !== false && (
      <PulseCell
        className="bg-white border-b border-b-neutral-200 group-hover:bg-neutral-50 min-w-0 overflow-hidden"
        data-testid={`activity-row-${activity.id}-provider`}
      >
        <div className="flex items-center w-full min-w-0 relative">
          <div className="flex flex-col min-w-0 flex-1">
            <Link
              href={getProfileUrl(activity.provider.type, activity.provider.provider_id)}
              scroll={false}
              className="text-link text-sm font-bold hover:underline truncate hover:text-sky-800"
              data-testid={`activity-row-${activity.id}-provider-link`}
              aria-label={`View profile for ${activity.provider.name}`}
            >
              {activity.provider.name}
            </Link>
            <span className="text-xs text-tertiary truncate" aria-label={`Specialty: ${activity.provider.specialty}`}>
              {activity.provider.specialty}
            </span>
          </div>
          <button
            type="button"
            onClick={() =>
              onOpenProfile({
                providerId: activity.provider.id,
                type: activity.provider.type,
              })
            }
            className="hidden group-hover:flex items-center gap-1 px-2 py-1 rounded-md border border-neutral-200 bg-white shadow-sm ml-2 hover:bg-neutral-50 flex-shrink-0"
            data-testid={`activity-row-${activity.id}-open-profile`}
            aria-label={`Open profile drawer for ${activity.provider.name}`}
          >
            <Icon
              icon={faArrowUpRightFromSquare}
              className="w-3 h-3 text-tertiary"
              aria-hidden="true"
            />
            <span className="text-xs font-bold text-tertiary">Open</span>
          </button>
        </div>
      </PulseCell>
      )}

      {/* Address */}
      {columnVisibilityModel.address !== false && (
      <PulseCell
        className="bg-white border-b border-b-neutral-200 group-hover:bg-neutral-50 min-w-0 overflow-hidden"
        data-testid={`activity-row-${activity.id}-address`}
      >
        <Tooltip
          content={activity.provider.address_line_1 || ''}
          position="top"
          delay={300}
          className="block w-full min-w-0"
        >
          <span
            className="block truncate cursor-pointer"
            aria-label={activity.provider.address_line_1 ? `Address: ${activity.provider.address_line_1}` : 'No address available'}
          >
            {activity.provider.address_line_1 || '-'}
          </span>
        </Tooltip>
      </PulseCell>
      )}

      {/* City */}
      {columnVisibilityModel.city !== false && (
      <PulseCell
        className="bg-white border-b border-b-neutral-200 group-hover:bg-neutral-50"
        data-testid={`activity-row-${activity.id}-city`}
      >
        <span aria-label={activity.provider.city ? `City: ${activity.provider.city}` : 'No city available'}>
          {activity.provider.city || '-'}
        </span>
      </PulseCell>
      )}

      {/* State */}
      {columnVisibilityModel.state !== false && (
      <PulseCell
        className="bg-white border-b border-b-neutral-200 group-hover:bg-neutral-50"
        data-testid={`activity-row-${activity.id}-state`}
      >
        <span aria-label={activity.provider.state ? `State: ${activity.provider.state}` : 'No state available'}>
          {activity.provider.state || '-'}
        </span>
      </PulseCell>
      )}

      {/* Representative (Manager only) */}
      {isManager && columnVisibilityModel.owner !== false && (
        <PulseCell
          className="bg-white border-b border-b-neutral-200 group-hover:bg-neutral-50"
          data-testid={`activity-row-${activity.id}-owner`}
        >
          <span aria-label={`Representative: ${activity.owner.first_name} ${activity.owner.last_name}`}>
            {`${activity.owner.first_name} ${activity.owner.last_name}`}
          </span>
        </PulseCell>
      )}

      {/* Activity Date */}
      {columnVisibilityModel.activity_date !== false && (
      <PulseCell
        className="bg-white border-b border-b-neutral-200 group-hover:bg-neutral-50"
        data-testid={`activity-row-${activity.id}-activity-date`}
      >
        <span aria-label={`Activity date ${formatActivityDate(activity.activity_date)}`}>
          {formatActivityDate(activity.activity_date)}
        </span>
      </PulseCell>
      )}

      {/* Created At */}
      {columnVisibilityModel.created_at !== false && (
      <PulseCell
        className="bg-white border-b border-b-neutral-200 group-hover:bg-neutral-50"
        data-testid={`activity-row-${activity.id}-created-at`}
      >
        <span aria-label={`Created ${daysSince(activity.created_at)}`}>
          {daysSince(activity.created_at)}
        </span>
      </PulseCell>
      )}

      {/* CRM Status (conditional) */}
      {showStatusColumn && columnVisibilityModel.status !== false && (
        <PulseCell
          className="bg-white border-b border-b-neutral-200 group-hover:bg-neutral-50"
          data-testid={`activity-row-${activity.id}-status`}
        >
          <div className="flex flex-col gap-1" role="region" aria-label="CRM status and actions">
            <MedRMStatus provider={activity.provider} />
            <PushToCRMGroup provider={activity.provider} />
          </div>
        </PulseCell>
      )}

      {/* Note */}
      {columnVisibilityModel.details !== false && (
      <PulseCell
        className="bg-white border-b border-b-neutral-200 group-hover:bg-neutral-50 min-w-0 overflow-hidden"
        data-testid={`activity-row-${activity.id}-details`}
      >
        <Tooltip
          content={activity.details}
          position="top"
          delay={300}
          className="block w-full min-w-0"
        >
          <span className="block truncate cursor-pointer" aria-label={`Note: ${activity.details}`}>
            {activity.details}
          </span>
        </Tooltip>
      </PulseCell>
      )}

      {/* Filler cell - matches PulseCell height */}
      <div className="h-12 bg-white border-b border-b-neutral-200 group-hover:bg-neutral-50" />
    </React.Fragment>
  );
};

export default ActivityTableRow;
